import { usePageBreadcrumbs } from "@/components/layout/breadcrumb-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuthProfile } from "@/hooks/use-auth-profile";
import { getClientFunctions } from "@/lib/firebase-client";
import { cn } from "@/lib/utils";
import { httpsCallable } from "firebase/functions";
import { Loader2, Play, Terminal, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// ─── Types ────────────────────────────────────────────────────────────────────

type LogLevel = "info" | "success" | "error" | "data";

type LogEntry = {
  id: string;
  timestamp: Date;
  level: LogLevel;
  message: string;
  data?: unknown;
};

type FieldDef = {
  key: string;
  label: string;
  type: "text" | "email" | "textarea" | "select" | "checkbox";
  placeholder?: string;
  required?: boolean;
  default?: string | boolean;
  options?: { value: string; label: string }[];
  width?: "sm" | "md" | "lg" | "full";
};

type FormValues = Record<string, string | boolean>;
type LogFn = (entry: Omit<LogEntry, "id" | "timestamp">) => void;

type TestDef = {
  id: string;
  category: string;
  label: string;
  description?: string;
  fields: FieldDef[];
  run: (values: FormValues, log: LogFn) => Promise<void>;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function callFn<T>(name: string, data: unknown): Promise<T> {
  const functions = getClientFunctions();
  if (!functions) throw new Error("Firebase functions unavailable");
  const callable = httpsCallable<unknown, T>(functions, name);
  return callable(data).then((r) => r.data);
}

const ROLE_OPTIONS = [
  { value: "viewer", label: "Viewer" },
  { value: "member", label: "Member" },
  { value: "owner", label: "Owner" },
];

// ─── Test Definitions ─────────────────────────────────────────────────────────
// Add new tests here. They will appear automatically in the sidebar grouped by category.

const TESTS: TestDef[] = [
  {
    id: "invitation-email-new-user",
    category: "Invitations",
    label: "Email — New User",
    description:
      'Sends invitation email with "Download the App" section (isNewUser=true). No Firestore writes.',
    fields: [
      { key: "email", label: "Recipient Email", type: "email", placeholder: "test@example.com", required: true, width: "md" },
      { key: "recipientName", label: "Recipient Name", type: "text", placeholder: "Jane Doe", width: "md" },
      { key: "role", label: "Role", type: "select", default: "member", options: ROLE_OPTIONS, width: "sm" },
    ],
    run: async (values, log) => {
      log({ level: "info", message: `Calling testInvitationEmail → ${values.email} [isNewUser=true]` });
      const result = await callFn("testInvitationEmail", { ...values, isNewUser: true });
      log({ level: "success", message: "Email dispatched successfully", data: result });
    },
  },
  {
    id: "invitation-push",
    category: "Invitations",
    label: "Push Notification",
    description: "Sends a test push notification to an existing user by email. User must have FCM tokens registered.",
    fields: [
      { key: "email", label: "Recipient Email", type: "email", placeholder: "user@example.com", required: true, width: "md" },
      { key: "title", label: "Title", type: "text", placeholder: "Test Notification", default: "Test Notification", width: "md" },
      { key: "body", label: "Body", type: "text", placeholder: "This is a test", default: "This is a test push notification", width: "lg" },
    ],
    run: async (values, log) => {
      log({ level: "info", message: `Calling testPushNotification → ${values.email}` });
      const result = await callFn("testPushNotification", values);
      log({ level: "success", message: "Push notification sent", data: result });
    },
  },
  {
    id: "recipe-import",
    category: "Recipes",
    label: "Import Recipe",
    description: "Scrapes and parses a recipe from a public URL using the recipe importer.",
    fields: [
      { key: "url", label: "Recipe URL", type: "text", placeholder: "https://example.com/recipe/pasta", required: true, width: "full" },
    ],
    run: async (values, log) => {
      log({ level: "info", message: `Calling importRecipe → ${values.url}` });
      const result = await callFn("importRecipe", { url: values.url });
      log({ level: "success", message: "Recipe imported", data: result });
    },
  },
];

// ─── Log line component ───────────────────────────────────────────────────────

const LOG_COLORS: Record<LogLevel, string> = {
  info: "text-zinc-300",
  success: "text-green-400",
  error: "text-red-400",
  data: "text-sky-300",
};

const LOG_ICONS: Record<LogLevel, string> = {
  info: "·",
  success: "✓",
  error: "✗",
  data: "»",
};

function LogLine({ entry }: { entry: LogEntry }) {
  const ms = String(entry.timestamp.getMilliseconds()).padStart(3, "0");
  const timeStr =
    entry.timestamp.toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }) +
    "." +
    ms;

  return (
    <div className={cn("flex gap-3 leading-relaxed min-w-0", LOG_COLORS[entry.level])}>
      <span className="text-zinc-600 shrink-0 select-none tabular-nums">{timeStr}</span>
      <span className="shrink-0 select-none w-3 text-center">{LOG_ICONS[entry.level]}</span>
      <div className="flex-1 min-w-0 break-words">
        <span>{entry.message}</span>
        {entry.data !== undefined && (
          <pre className="mt-1 text-sky-300 whitespace-pre-wrap text-[10px] opacity-80 leading-relaxed">
            {JSON.stringify(entry.data, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

// ─── Field renderer ───────────────────────────────────────────────────────────

const WIDTH_CLASSES: Record<NonNullable<FieldDef["width"]>, string> = {
  sm: "w-28",
  md: "w-44",
  lg: "w-64",
  full: "w-full",
};

function FormField({
  field,
  value,
  disabled,
  onChange,
}: {
  field: FieldDef;
  value: string | boolean;
  disabled: boolean;
  onChange: (v: string | boolean) => void;
}) {
  const widthClass = WIDTH_CLASSES[field.width ?? "md"];

  return (
    <div className={cn("space-y-1", field.width === "full" ? "w-full" : widthClass)}>
      <Label className="text-xs">
        {field.label}
        {field.required && <span className="text-destructive ml-0.5">*</span>}
      </Label>

      {field.type === "select" ? (
        <Select
          value={String(value ?? field.default ?? "")}
          onValueChange={(v) => onChange(v)}
          disabled={disabled}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {field.options?.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : field.type === "checkbox" ? (
        <div className="flex items-center h-8 gap-2">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            className="h-4 w-4"
          />
        </div>
      ) : field.type === "textarea" ? (
        <textarea
          className="w-full text-xs border rounded-md p-2 font-mono min-h-[60px] resize-y bg-background"
          value={String(value ?? "")}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      ) : (
        <Input
          type={field.type}
          className="h-8 text-xs"
          placeholder={field.placeholder}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function getDefaultValues(fields: FieldDef[]): FormValues {
  return Object.fromEntries(fields.map((f) => [f.key, f.default ?? (f.type === "checkbox" ? false : "")]));
}

export default function AdminTestHarnessPage() {
  const breadcrumbs = useMemo(
    () => [
      { label: "Recipe Boxes", href: "/home" },
      { label: "Admin", href: "/admin" },
      { label: "Test Harness" },
    ],
    [],
  );
  usePageBreadcrumbs(breadcrumbs);

  const { user, profile, loading } = useAuthProfile();
  const navigate = useNavigate();

  const [selectedTestId, setSelectedTestId] = useState<string>(TESTS[0].id);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>(() => getDefaultValues(TESTS[0].fields));
  const logEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
    else if (!loading && user && profile && !profile.isAdmin) navigate("/home");
  }, [loading, user, profile, navigate]);

  const selectedTest = useMemo(() => TESTS.find((t) => t.id === selectedTestId) ?? TESTS[0], [selectedTestId]);

  // Reset form when test selection changes
  useEffect(() => {
    setFormValues(getDefaultValues(selectedTest.fields));
  }, [selectedTestId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll log to bottom
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logEntries]);

  const addLogEntry = useCallback((entry: Omit<LogEntry, "id" | "timestamp">) => {
    setLogEntries((prev) => [...prev, { ...entry, id: crypto.randomUUID(), timestamp: new Date() }]);
  }, []);

  const handleRun = useCallback(async () => {
    setIsRunning(true);
    addLogEntry({ level: "info", message: `▶ Starting: ${selectedTest.label}` });
    try {
      await selectedTest.run(formValues, addLogEntry);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      addLogEntry({ level: "error", message: `Failed: ${message}`, data: err });
    } finally {
      setIsRunning(false);
      addLogEntry({ level: "info", message: "─────────────────────────────────" });
    }
  }, [selectedTest, formValues, addLogEntry]);

  const handleFieldChange = useCallback((key: string, value: string | boolean) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const categories = useMemo(() => {
    const seen = new Set<string>();
    return TESTS.reduce<string[]>((acc, t) => {
      if (!seen.has(t.category)) {
        seen.add(t.category);
        acc.push(t.category);
      }
      return acc;
    }, []);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user || !profile || !profile.isAdmin) return null;

  return (
    <div className="flex flex-col h-[calc(100vh-130px)] border rounded-lg overflow-hidden">
      <div className="flex flex-1 min-h-0">

        {/* ── Left sidebar: test list ─────────────────────── */}
        <div className="w-52 border-r flex flex-col shrink-0 bg-muted/20">
          <div className="px-4 py-3 border-b">
            <div className="flex items-center gap-2">
              <Terminal className="h-4 w-4 text-muted-foreground" />
              <span className="font-semibold text-sm">Test Suite</span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto py-2">
            {categories.map((category) => (
              <div key={category}>
                <p className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  {category}
                </p>
                {TESTS.filter((t) => t.category === category).map((test) => (
                  <button
                    key={test.id}
                    onClick={() => setSelectedTestId(test.id)}
                    className={cn(
                      "w-full text-left px-4 py-2 text-sm transition-colors hover:bg-muted",
                      selectedTestId === test.id
                        ? "bg-muted font-medium border-r-2 border-primary text-foreground"
                        : "text-muted-foreground",
                    )}
                  >
                    {test.label}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* ── Right panel ──────────────────────────────────── */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0">

          {/* Input section — fixed height */}
          <div className="border-b px-5 py-4 shrink-0 bg-background">
            <div className="mb-3">
              <h2 className="font-semibold text-sm">{selectedTest.label}</h2>
              {selectedTest.description && (
                <p className="text-xs text-muted-foreground mt-0.5">{selectedTest.description}</p>
              )}
            </div>

            <div className="flex flex-wrap gap-3 items-end">
              {selectedTest.fields.map((field) => (
                <FormField
                  key={field.key}
                  field={field}
                  value={formValues[field.key] ?? (field.default ?? (field.type === "checkbox" ? false : ""))}
                  disabled={isRunning}
                  onChange={(v) => handleFieldChange(field.key, v)}
                />
              ))}

              <Button size="sm" onClick={handleRun} disabled={isRunning} className="h-8 gap-1.5 self-end">
                {isRunning ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Play className="h-3.5 w-3.5" />
                )}
                {isRunning ? "Running..." : "Run"}
              </Button>
            </div>
          </div>

          {/* Log panel — takes remaining height */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between px-4 py-1.5 border-b bg-zinc-900 shrink-0">
              <span className="text-[10px] font-mono font-semibold tracking-widest uppercase text-zinc-500">
                Output
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-zinc-500 hover:text-zinc-200 hover:bg-zinc-700"
                onClick={() => setLogEntries([])}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                <span className="text-[10px]">Clear</span>
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto bg-zinc-950 px-4 py-3 font-mono text-xs space-y-0.5">
              {logEntries.length === 0 ? (
                <span className="text-zinc-600">No output yet. Select a test and press Run.</span>
              ) : (
                logEntries.map((entry) => <LogLine key={entry.id} entry={entry} />)
              )}
              <div ref={logEndRef} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
